import { useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Upload, FolderSearch, Wand2, ArrowRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function Workflow({ className = '' }: { className?: string }) {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const ctx = gsap.context(() => {
      const scrollTl = gsap.timeline({
        scrollTrigger: { trigger: section, start: 'top top', end: '+=70%', pin: true, scrub: 0.6 },
      });
      scrollTl.fromTo(headerRef.current, { y: 30, opacity: 0 }, { y: 0, opacity: 1, ease: 'none' }, 0);
      scrollTl.fromTo(stepsRef.current?.children || [], { y: 40, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.04, ease: 'none' }, 0.05);
      scrollTl.fromTo(headerRef.current, { y: 0, opacity: 1 }, { y: '-10vh', opacity: 0, ease: 'power2.in' }, 0.7);
      scrollTl.fromTo(stepsRef.current?.children || [], { y: 0, opacity: 1 }, { y: '10vh', opacity: 0, stagger: 0.02, ease: 'power2.in' }, 0.72);
    }, section);

    return () => ctx.revert();
  }, []);

  const steps = [
    { number: '01', icon: Upload, title: 'Upload Once', description: 'Add logos, headshots, and brand assets to your library. Organize by artist, venue, or event.', color: '#2EC4B6' },
    { number: '02', icon: FolderSearch, title: 'Auto-Organize', description: 'Event.Tools automatically links assets to entities. DJ logos go with DJ profiles.', color: '#2EC4B6' },
    { number: '03', icon: Wand2, title: 'Generate Forever', description: 'Create flyers in seconds. AI always uses the right assets—no hunting, no asking.', color: '#FF6B4A' },
  ];

  return (
    <section ref={sectionRef} id="workflow" className={`pinned-section bg-[#0B0F10] flex items-center ${className}`}>
      <div className="w-full px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div ref={headerRef} className="text-center mb-10" style={{ willChange: 'transform, opacity' }}>
            <span className="font-mono text-xs uppercase tracking-[0.12em] text-[#2EC4B6] mb-4 block">How It Works</span>
            <h2 className="font-display font-bold text-[clamp(36px,4.5vw,64px)] leading-tight text-white">
              Upload once.<br />
              <span className="text-[#2EC4B6]">Generate forever.</span>
            </h2>
          </div>
          <div ref={stepsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step, i) => (
              <div key={i} className="relative" style={{ willChange: 'transform, opacity' }}>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-full w-full h-px">
                    <div className="w-full h-full bg-gradient-to-r from-[#2EC4B6]/50 to-transparent" />
                    <ArrowRight size={16} className="absolute right-0 top-1/2 -translate-y-1/2 text-[#2EC4B6]" />
                  </div>
                )}
                <div className="bg-[#1a1f21] rounded-xl p-8 border border-white/10 h-full">
                  <span className="font-display font-bold text-5xl mb-6 block" style={{ color: `${step.color}40` }}>{step.number}</span>
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ backgroundColor: `${step.color}20` }}>
                    <step.icon size={28} style={{ color: step.color }} />
                  </div>
                  <h3 className="font-display font-semibold text-xl text-white mb-3">{step.title}</h3>
                  <p className="text-[#A9B4B8] leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
